import type { ShiftPayInput } from '../schads';
import { payrollBreakMinutesForEngine } from './break-treatment';

/** Whole-shift times used for majority / commencement / character classification. */
export type ShiftClassificationGeometry = {
  startTime: Date;
  endTime: Date;
  breakMinutes: number;
};

/**
 * Resolve classification geometry for a priced fragment. When settlement
 * segmented pricing supplies a shift envelope, whole-shift triggers classify
 * on the envelope while fragment start/end still drive worked hours.
 */
export function shiftClassificationGeometry(
  input: Pick<
    ShiftPayInput,
    | 'startTime'
    | 'endTime'
    | 'breakMinutes'
    | 'breakTreatment'
    | 'breakSemantics'
    | 'shiftEnvelopeStartTime'
    | 'shiftEnvelopeEndTime'
    | 'shiftEnvelopeBreakMinutes'
  >,
): ShiftClassificationGeometry {
  const startTime = input.shiftEnvelopeStartTime ?? input.startTime;
  const endTime = input.shiftEnvelopeEndTime ?? input.endTime;
  const rawBreakMinutes =
    input.shiftEnvelopeBreakMinutes !== undefined
      ? input.shiftEnvelopeBreakMinutes
      : (input.breakMinutes ?? 0);
  return {
    startTime,
    endTime,
    breakMinutes: payrollBreakMinutesForEngine(
      rawBreakMinutes,
      input.breakTreatment,
      input.breakSemantics,
    ),
  };
}

/** Shift-cumulative meal-OT threshold crossings for fragmented pricing. */
export function overtimeMealAllowanceCrossings(input: {
  fragmentOvertimeHours: number;
  priorOvertimeHoursThisShift?: number | null;
  firstMealThresholdHours: number;
  secondMealThresholdHours: number;
}): { emitFirstMeal: boolean; emitSecondMeal: boolean } {
  const prior = Math.max(0, input.priorOvertimeHoursThisShift ?? 0);
  const fragment = Math.max(0, input.fragmentOvertimeHours);
  const cumulative = prior + fragment;
  const first = input.firstMealThresholdHours;
  const second = input.secondMealThresholdHours;
  return {
    emitFirstMeal: cumulative > first && prior <= first,
    emitSecondMeal: cumulative > second && prior <= second,
  };
}
